import type { VisualEditorAlign } from '../editor-types';
import { closestFromNode, elementFromSelectionNode } from './selection';
import { getNearestCell } from './tables';

const UNWRAP_TAGS = new Set(['span', 'font', 'b', 'i', 'u', 'strong', 'em', 'strike', 's', 'mark']);
const STRUCTURE_TAGS = new Set(['table', 'thead', 'tbody', 'tfoot', 'tr', 'ul', 'ol', 'li', 'a', 'img', 'br', 'hr']);
const ALIGN_BLOCK_SELECTOR = 'p, h1, h2, h3, h4, h5, h6, div, li, td, th, blockquote, pre';

const unwrapElement = (element: Element): void => {
    const parent = element.parentNode;
    if (!parent) {
        element.remove();
        return;
    }

    while (element.firstChild) {
        parent.insertBefore(element.firstChild, element);
    }
    parent.removeChild(element);
};

const clearElementFormatting = (element: Element): void => {
    const tag = element.tagName.toLowerCase();
    if (STRUCTURE_TAGS.has(tag)) return;

    element.removeAttribute('style');
    element.removeAttribute('class');
    element.removeAttribute('align');
    element.removeAttribute('color');
    element.removeAttribute('face');
    element.removeAttribute('size');
};

const collectElementsInRange = (range: Range): Element[] => {
    const root = range.commonAncestorContainer;
    const rootElement = elementFromSelectionNode(root);
    if (!rootElement) return [];

    if (range.collapsed) {
        const elements: Element[] = [];
        let current: Element | null = rootElement;
        while (current && current !== current.ownerDocument?.body) {
            elements.push(current);
            if (current.matches(ALIGN_BLOCK_SELECTOR)) break;
            current = current.parentElement;
        }
        return elements;
    }

    const elements: Element[] = [];
    if (rootElement !== rootElement.ownerDocument?.body) {
        elements.push(rootElement);
    }

    const walker = rootElement.ownerDocument?.createTreeWalker(rootElement, NodeFilter.SHOW_ELEMENT);
    let node = walker?.currentNode as Element | null;
    while (node) {
        if (node !== rootElement && range.intersectsNode(node)) {
            elements.push(node);
        }
        node = walker?.nextNode() as Element | null;
    }

    return elements;
};

export const clearFormatting = (_document: Document, range: Range | null): void => {
    if (!range) return;

    const elements = collectElementsInRange(range);
    const unwrapTargets = elements.filter((element) => UNWRAP_TAGS.has(element.tagName.toLowerCase())).reverse();

    elements.forEach(clearElementFormatting);
    unwrapTargets.forEach((element) => {
        if (element.isConnected) unwrapElement(element);
    });
};

export const applyTextAlign = (_document: Document, range: Range | null, align: VisualEditorAlign): void => {
    const node = range?.startContainer ?? null;
    const cell = getNearestCell(node);
    const block = cell ?? (closestFromNode(node, ALIGN_BLOCK_SELECTOR) as HTMLElement | null);
    if (!block) return;

    block.style.textAlign = align;
    if (align === 'justify') {
        block.removeAttribute('align');
    } else {
        block.setAttribute('align', align);
    }
};
